/**
 * /funding-check state machine — pure, so the flow is unit-testable without
 * a DOM. The route renders whatever `step` says; this file owns the
 * transitions, the sessionStorage round-trip (a reload after the result must
 * not lose five answers) and the request body the API expects.
 *
 * Invariants the transitions encode:
 *   - the state selector is the LAST question (q4 → state);
 *   - the result renders in full BEFORE anything is asked (result → trade);
 *   - declining from either capture screen lands on `declined`, which can only
 *     route back to `capture` (the "changed my mind" path) or reset.
 */
import {
  FUNDING_AWARENESS_OPTIONS,
  FUNDING_CHECK_CONSENT,
  FUNDING_GRADE_BAND_OPTIONS,
  FUNDING_HOW_FIND_OPTIONS,
  FUNDING_SCHOOL_TYPE_OPTIONS,
  FUNDING_STATES,
  fundingStateByAbbr,
  type FundingAwareness,
  type FundingGradeBand,
  type FundingHowFind,
  type FundingLeadSurface,
  type FundingSchoolType,
  type FundingState,
} from "@stratacademy/core";

export type Step =
  | "intro"
  | "q1"
  | "q2"
  | "q3"
  | "q4"
  | "state"
  | "checking"
  | "result"
  | "trade"
  | "capture"
  | "declined"
  | "done";

export const STEPS: readonly Step[] = [
  "intro",
  "q1",
  "q2",
  "q3",
  "q4",
  "state",
  "checking",
  "result",
  "trade",
  "capture",
  "declined",
  "done",
];

export interface Answers {
  awareness?: FundingAwareness;
  grade_band?: FundingGradeBand;
  school_type?: FundingSchoolType;
  how_find?: FundingHowFind;
  /** 2-letter code. */
  state?: string;
}

export type AnswerField = Exclude<keyof Answers, "state">;

export interface QuizState {
  step: Step;
  answers: Answers;
}

export const INITIAL_STATE: QuizState = { step: "intro", answers: {} };

export type Action =
  | { type: "START" }
  | { type: "ANSWER"; field: AnswerField; key: string }
  | { type: "PICK_STATE"; abbr: string }
  | { type: "BACK" }
  | { type: "CHECK_DONE" }
  | { type: "CONTINUE" }
  | { type: "DECLINE" }
  | { type: "CHANGE_MIND" }
  | { type: "SUBMITTED" }
  | { type: "RESET" };

/** q1..q4 in page order → the answer field each fills. */
const QUESTION_FIELDS: Record<"q1" | "q2" | "q3" | "q4", AnswerField> = {
  q1: "awareness",
  q2: "grade_band",
  q3: "school_type",
  q4: "how_find",
};
const QUESTION_ORDER = ["q1", "q2", "q3", "q4", "state"] as const;

const VALID_KEYS: Record<AnswerField, readonly string[]> = {
  awareness: FUNDING_AWARENESS_OPTIONS,
  grade_band: FUNDING_GRADE_BAND_OPTIONS,
  school_type: FUNDING_SCHOOL_TYPE_OPTIONS,
  how_find: FUNDING_HOW_FIND_OPTIONS,
};

/** 1-based question number for the answered event (state selector is 5). */
export function questionNumber(field: AnswerField | "state"): number {
  if (field === "state") return 5;
  const step = (Object.keys(QUESTION_FIELDS) as Array<keyof typeof QUESTION_FIELDS>).find(
    (q) => QUESTION_FIELDS[q] === field,
  )!;
  return QUESTION_ORDER.indexOf(step) + 1;
}

export function fundingReducer(state: QuizState, action: Action): QuizState {
  switch (action.type) {
    case "START":
      return state.step === "intro" ? { ...state, step: "q1" } : state;
    case "ANSWER": {
      if (!(state.step in QUESTION_FIELDS)) return state;
      const expected = QUESTION_FIELDS[state.step as keyof typeof QUESTION_FIELDS];
      if (expected !== action.field || !VALID_KEYS[action.field].includes(action.key)) return state;
      const i = QUESTION_ORDER.indexOf(state.step as (typeof QUESTION_ORDER)[number]);
      return {
        step: QUESTION_ORDER[i + 1]!,
        answers: { ...state.answers, [action.field]: action.key },
      };
    }
    case "PICK_STATE": {
      if (state.step !== "state") return state;
      const st = fundingStateByAbbr(action.abbr);
      if (!st) return state;
      return { step: "checking", answers: { ...state.answers, state: st.abbr } };
    }
    case "BACK": {
      // Only within the questions: q2..state step back one, answers kept so
      // the parent sees what they picked and can change it. Nothing else has
      // a "back" — the brief is the destination, not a step.
      const i = QUESTION_ORDER.indexOf(state.step as (typeof QUESTION_ORDER)[number]);
      if (i <= 0) return state;
      return { ...state, step: QUESTION_ORDER[i - 1]! };
    }
    case "CHECK_DONE":
      return state.step === "checking" ? { ...state, step: "result" } : state;
    case "CONTINUE":
      return state.step === "result" ? { ...state, step: "trade" } : state;
    case "DECLINE":
      return state.step === "trade" || state.step === "capture"
        ? { ...state, step: "declined" }
        : state;
    case "CHANGE_MIND":
      return state.step === "declined" ? { ...state, step: "capture" } : state;
    case "SUBMITTED":
      return state.step === "trade" || state.step === "capture"
        ? { ...state, step: "done" }
        : state;
    case "RESET":
      return INITIAL_STATE;
  }
}

/** Progress-bar fill per step (percent). */
export function progressFor(step: Step): number {
  switch (step) {
    case "intro":
      return 0;
    case "q1":
      return 20;
    case "q2":
      return 40;
    case "q3":
      return 60;
    case "q4":
      return 80;
    case "state":
      return 90;
    default:
      return 100;
  }
}

// ── Persistence ─────────────────────────────────────────────────────────────
// sessionStorage: survives a reload (the common "did that just lose my
// answers?" moment) but not a new tab or a closed browser. The phone number
// is deliberately never stored — only step + answers.

export const STORAGE_KEY = "sa:funding-check";

function isValidAnswers(a: unknown): a is Answers {
  if (!a || typeof a !== "object" || Array.isArray(a)) return false;
  const o = a as Record<string, unknown>;
  for (const field of Object.keys(VALID_KEYS) as AnswerField[]) {
    const v = o[field];
    if (v !== undefined && !(typeof v === "string" && VALID_KEYS[field].includes(v))) return false;
  }
  if (o.state !== undefined && !(typeof o.state === "string" && fundingStateByAbbr(o.state))) {
    return false;
  }
  return true;
}

/** The answers a visitor must already have given to be standing on `step`. */
export function hasAnswersFor(step: Step, a: Answers): boolean {
  const need: Array<keyof Answers> = [];
  switch (step) {
    case "intro":
    case "q1":
      break;
    case "q2":
      need.push("awareness");
      break;
    case "q3":
      need.push("awareness", "grade_band");
      break;
    case "q4":
      need.push("awareness", "grade_band", "school_type");
      break;
    case "state":
      need.push("awareness", "grade_band", "school_type", "how_find");
      break;
    default:
      // From the brief on, only the state is load-bearing: a re-send URL
      // (?state=…) opens here having asked nothing, and the API takes every
      // other answer as optional.
      need.push("state");
  }
  return need.every((k) => a[k] !== undefined);
}

export function loadPersisted(storage: Storage | null = safeSession()): QuizState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { step, answers } = parsed as { step?: unknown; answers?: unknown };
    if (typeof step !== "string" || !STEPS.includes(step as Step)) return null;
    if (!isValidAnswers(answers)) return null;
    // A reload mid-"checking" or on "done" lands on the natural screen.
    const resumed: Step =
      step === "checking" ? "result" : step === "done" ? "intro" : (step as Step);
    if (resumed === "intro") return null;
    // A screen is only resumable with every answer the flow would have
    // collected to reach it — otherwise the page would render a brief with no
    // state, so the stash is discarded and the visitor starts clean.
    if (!hasAnswersFor(resumed, answers)) return null;
    return { step: resumed, answers };
  } catch {
    return null;
  }
}

export function persist(state: QuizState, storage: Storage | null = safeSession()): void {
  if (!storage) return;
  try {
    if (state.step === "intro" || state.step === "done") storage.removeItem(STORAGE_KEY);
    else storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — the flow still works in memory */
  }
}

export function clearPersisted(storage: Storage | null = safeSession()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function safeSession(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// ── Request body ────────────────────────────────────────────────────────────

export interface LeadBody {
  phone: string;
  state: string;
  /** Only the answers actually given — a re-send URL visit never saw Q1/Q4. */
  answers: {
    awareness?: FundingAwareness;
    grade_band?: FundingGradeBand;
    school_type?: FundingSchoolType;
    how_find?: FundingHowFind;
  };
  consent: { checked: true; version: string };
  page_url: string;
  attribution?: Record<string, string>;
  /**
   * The Meta signal block: the browser pixel's dedup `event_id` (shared by the
   * `Lead` / `funding_phone_submitted` pixel events) plus Meta's `_fbp`/`_fbc`
   * cookies, so a server-side Conversions API mirror can be sent later and
   * counted once. Stored in the row's `params`.
   */
  meta?: MetaSignal;
  /** Which landing page captured the lead; the API defaults to the quiz. */
  surface?: FundingLeadSurface;
}

export interface MetaSignal {
  event_id: string;
  fbp?: string;
  fbc?: string;
}

/**
 * The POST /v1/funding-check/leads body, or null when there is no state (the
 * machine makes that impossible in practice; this is the type-level guard).
 * The server derives the state's flags itself — only the code goes.
 */
export function buildLeadBody(
  answers: Answers,
  phoneE164: string,
  pageUrl: string,
  attribution: Record<string, string> | null,
  meta?: MetaSignal,
  surface?: FundingLeadSurface,
): LeadBody | null {
  const { awareness, grade_band, school_type, how_find, state } = answers;
  if (!state) return null;
  const given: LeadBody["answers"] = {};
  if (awareness) given.awareness = awareness;
  if (grade_band) given.grade_band = grade_band;
  if (school_type) given.school_type = school_type;
  if (how_find) given.how_find = how_find;
  const body: LeadBody = {
    phone: phoneE164,
    state,
    answers: given,
    consent: { checked: true, version: FUNDING_CHECK_CONSENT.version },
    page_url: pageUrl,
  };
  if (attribution && Object.keys(attribution).length > 0) body.attribution = attribution;
  if (meta) body.meta = meta;
  if (surface) body.surface = surface;
  return body;
}

/**
 * The raw `utm_*` pairs plus `fbclid` from the attribution stash — what the
 * intro's viewed/started events carry so entry bounce can be read per ad.
 */
export function utmProps(attribution: Record<string, string> | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!attribution) return out;
  for (const [k, v] of Object.entries(attribution)) {
    if ((k.startsWith("utm_") || k === "fbclid") && v) out[k] = v;
  }
  return out;
}

// ── URL state ───────────────────────────────────────────────────────────────
// The brief is re-sent in texts and retargeting ads, so its URL must be enough
// to render it: `/funding-check?state=Texas&school=homeschool&grade=6-8`. The
// public names and values are shared with /funding-brief and with the SMS
// templates; internally the quiz keeps its stable keys. utm_* / fbclid ride
// alongside untouched.

export const URL_PARAM = { state: "state", school: "school", grade: "grade" } as const;

const GRADE_TO_URL: Record<FundingGradeBand, string> = {
  k_2: "k-2",
  "3_5": "3-5",
  "6_8": "6-8",
  "9_12": "9-12",
  multiple: "multiple",
};
const GRADE_FROM_URL: Record<string, FundingGradeBand> = Object.fromEntries(
  Object.entries(GRADE_TO_URL).map(([k, v]) => [v, k as FundingGradeBand]),
);

/** A state by full name or 2-letter code, case-insensitive. */
export function fundingStateByNameOrAbbr(value: string): FundingState | undefined {
  const v = value.trim().toLowerCase();
  if (!v) return undefined;
  return fundingStateByAbbr(v) ?? FUNDING_STATES.find((s) => s.name.toLowerCase() === v);
}

/**
 * The answers a search string carries, or null when it names no valid state
 * (the one required key). An unknown `school` or `grade` is ignored, not
 * fatal — a mistyped ad parameter must never block the brief.
 */
export function answersFromSearch(search: string | URLSearchParams): Answers | null {
  const p = typeof search === "string" ? new URLSearchParams(search) : search;
  const st = fundingStateByNameOrAbbr(p.get(URL_PARAM.state) ?? "");
  if (!st) return null;
  const answers: Answers = { state: st.abbr };
  const school = (p.get(URL_PARAM.school) ?? "").trim().toLowerCase();
  if ((FUNDING_SCHOOL_TYPE_OPTIONS as readonly string[]).includes(school)) {
    answers.school_type = school as FundingSchoolType;
  }
  const grade = GRADE_FROM_URL[(p.get(URL_PARAM.grade) ?? "").trim().toLowerCase()];
  if (grade) answers.grade_band = grade;
  return answers;
}

/** The current search with the answers written in; every other key kept. */
export function searchWithAnswers(
  current: string | URLSearchParams,
  answers: Answers,
): URLSearchParams {
  const p = new URLSearchParams(typeof current === "string" ? current : current.toString());
  const st = answers.state ? fundingStateByAbbr(answers.state) : undefined;
  if (st) p.set(URL_PARAM.state, st.name);
  else p.delete(URL_PARAM.state);
  if (answers.school_type) p.set(URL_PARAM.school, answers.school_type);
  else p.delete(URL_PARAM.school);
  if (answers.grade_band) p.set(URL_PARAM.grade, GRADE_TO_URL[answers.grade_band]);
  else p.delete(URL_PARAM.grade);
  return p;
}

/** A visit whose URL already names the state opens on the brief, nothing asked. */
export function seedFromSearch(search: string | URLSearchParams): QuizState | null {
  const answers = answersFromSearch(search);
  return answers ? { step: "result", answers } : null;
}
